/**
 * Theme Management Utilities
 * Integrates with SDK's HtmlThemeTemplates for theme detection and application
 */

export interface ThemeConfig {
  name: string;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  container_bg: string;
  text_color: string;
  accent_color: string;
}

/**
 * Available themes matching SDK themes
 */
export const AVAILABLE_THEMES: Record<string, ThemeConfig> = {
  educational: {
    name: 'Educational',
    primary_color: '#3b82f6',
    secondary_color: '#10b981',
    background_color: '#f8fafc',
    container_bg: '#ffffff',
    text_color: '#1e293b',
    accent_color: '#e0e7ff',
  },
  modern: {
    name: 'Modern',
    primary_color: '#8b5cf6',
    secondary_color: '#06b6d4',
    background_color: '#fafafa',
    container_bg: '#ffffff',
    text_color: '#18181b',
    accent_color: '#f3f4f6',
  },
  corporate: {
    name: 'Corporate',
    primary_color: '#059669',
    secondary_color: '#0891b2',
    background_color: '#f9fafb',
    container_bg: '#ffffff',
    text_color: '#111827',
    accent_color: '#ecfdf5',
  },
  dark: {
    name: 'Dark',
    primary_color: '#60a5fa',
    secondary_color: '#34d399',
    background_color: '#1f2937',
    container_bg: '#374151',
    text_color: '#f9fafb',
    accent_color: '#4b5563',
  },
  minimal: {
    name: 'Minimal',
    primary_color: '#6b7280',
    secondary_color: '#9ca3af',
    background_color: '#ffffff',
    container_bg: '#ffffff',
    text_color: '#374151',
    accent_color: '#f3f4f6',
  },
};

/**
 * Detect theme from HTML content by analyzing colors
 */
export function detectTheme(html: string): string {
  // Extract background color from body style
  const bodyBgMatch = html.match(/body\s*{\s*[^}]*background-color:\s*([^;]+);/);
  const bodyBg = bodyBgMatch?.[1]?.trim();

  // Match against known themes
  for (const [themeName, config] of Object.entries(AVAILABLE_THEMES)) {
    if (bodyBg === config.background_color) {
      return themeName;
    }
  }

  // Default to educational
  return 'educational';
}

/**
 * Extract title from HTML content
 */
export function extractTitle(html: string): string {
  const titleMatch = html.match(/<title>(.*?)<\/title>/i);
  return titleMatch ? titleMatch[1] : 'Content';
}

/**
 * Extract body content from HTML
 */
export function extractBodyContent(html: string): string {
  // Try to extract body content
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    return bodyMatch[1];
  }

  // If no body tag, return the HTML as-is (might already be body content)
  return html;
}

/**
 * Detect if content is RTL (Arabic, Hebrew, Persian, Urdu)
 */
export function isRTLLanguage(html: string): boolean {
  // Check for Arabic, Hebrew, Persian, Urdu characters
  const rtlPattern = /[\u0600-\u06FF\u0750-\u077F\u0590-\u05FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  return rtlPattern.test(html);
}

/**
 * Generate themed CSS for a given theme
 */
export function generateThemeCSS(themeName: string, isRTL: boolean = false): string {
  const theme = AVAILABLE_THEMES[themeName] || AVAILABLE_THEMES.educational;
  const direction = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  return `
/* ${theme.name} Theme CSS */
body {
  background-color: ${theme.background_color};
  color: ${theme.text_color};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Noto Sans Arabic', 'Arial', sans-serif;
  line-height: 1.6;
  margin: 0;
  padding: 20px;
  direction: ${direction};
  text-align: ${textAlign};
}

.content-container {
  background-color: ${theme.container_bg};
  border-radius: 8px;
  padding: 24px;
  max-width: 1000px;
  margin: 0 auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
}

h1 {
  color: ${theme.primary_color};
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  border-bottom: 2px solid ${theme.accent_color};
  padding-bottom: 0.5rem;
}

h2 {
  color: ${theme.primary_color};
  font-size: 1.75rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem 0;
  border-bottom: 1px solid ${theme.accent_color};
  padding-bottom: 0.25rem;
}

h3 {
  color: ${theme.secondary_color};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.25rem 0 0.75rem 0;
}

p {
  margin-bottom: 1rem;
  color: ${theme.text_color};
}

.learning-objectives {
  background-color: ${theme.accent_color};
  ${isRTL ? `border-right: 4px solid ${theme.primary_color};` : `border-left: 4px solid ${theme.primary_color};`}
  padding: 1rem;
  margin: 1.5rem 0;
  ${isRTL ? 'border-radius: 4px 0 0 4px;' : 'border-radius: 0 4px 4px 0;'}
}

.learning-objectives h2 {
  margin-top: 0;
  border: none;
  padding-bottom: 0;
}

.learning-objectives ul {
  margin: 0.5rem 0 0 0;
  ${isRTL ? 'padding-right: 1.5rem;' : 'padding-left: 1.5rem;'}
}

.learning-objectives li {
  margin-bottom: 0.5rem;
}

.example-box {
  background-color: ${theme.accent_color};
  border: 1px solid ${theme.primary_color};
  border-radius: 6px;
  padding: 1rem;
  margin: 1.5rem 0;
}

.example-box h3 {
  margin-top: 0;
  color: ${theme.primary_color};
}

.learning-checkpoint {
  background-color: ${themeName === 'educational' ? '#ecfdf5' : theme.accent_color};
  border: 1px solid ${theme.secondary_color};
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
}

.checkpoint-type {
  font-weight: 600;
  color: ${theme.secondary_color};
  margin-bottom: 0.5rem;
}

.question {
  font-weight: 500;
  margin-bottom: 1rem;
  color: ${theme.text_color};
}

.answer {
  background-color: ${theme.container_bg};
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 0.5rem;
  ${isRTL ? `border-right: 3px solid ${theme.secondary_color};` : `border-left: 3px solid ${theme.secondary_color};`}
}

.extension {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${theme.accent_color};
  font-style: italic;
}

.reveal-btn {
  background-color: ${theme.primary_color};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.reveal-btn:hover {
  background-color: ${theme.secondary_color};
}

/* Code Snippet Styling */
.skillzen-code-snippet {
  background: ${themeName === 'dark' ? '#1e293b' : '#ffffff'};
  border: 2px solid ${themeName === 'dark' ? '#374151' : '#e1e8ed'};
  border-radius: 12px;
  margin: 24px 0;
  box-shadow: 0 4px 12px rgba(0,0,0,${themeName === 'dark' ? '0.3' : '0.08'});
  overflow: hidden;
  font-family: 'Fira Code', 'SF Mono', 'Monaco', 'Consolas', 'Liberation Mono', monospace;
}

.skillzen-code-header {
  background: ${themeName === 'dark' ? 'linear-gradient(135deg, #374151 0%, #4b5563 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${themeName === 'dark' ? '#f3f4f6' : '#ffffff'};
  padding: 12px 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid ${themeName === 'dark' ? '#4b5563' : '#e1e8ed'};
}

.skillzen-code-content {
  padding: 20px;
  font-size: 14px;
  line-height: 1.6;
  overflow-x: auto;
  background: ${themeName === 'dark' ? '#1e293b' : '#fafbfc'};
}

.skillzen-code-line {
  display: block;
  padding: 2px 0;
  position: relative;
}

.skillzen-code-line::before {
  content: attr(data-line);
  color: ${themeName === 'dark' ? '#6b7280' : '#9ca3af'};
  width: 30px;
  display: inline-block;
  text-align: ${isRTL ? 'left' : 'right'};
  ${isRTL ? 'margin-left: 16px;' : 'margin-right: 16px;'}
  font-size: 12px;
  user-select: none;
}

/* Syntax Highlighting Classes */
.skillzen-keyword {
  color: ${themeName === 'dark' ? '#f472b6' : '#d73a49'};
  font-weight: bold;
}

.skillzen-string {
  color: ${themeName === 'dark' ? '#6ee7b7' : '#032f62'};
}

.skillzen-number {
  color: ${themeName === 'dark' ? '#fbbf24' : '#005cc5'};
}

.skillzen-function {
  color: ${themeName === 'dark' ? '#a78bfa' : '#6f42c1'};
}

.skillzen-variable {
  color: ${themeName === 'dark' ? '#fb923c' : '#e36209'};
}

.skillzen-comment {
  color: ${themeName === 'dark' ? '#9ca3af' : '#6a737d'};
  font-style: italic;
}

.skillzen-operator {
  color: ${themeName === 'dark' ? '#e5e7eb' : '#24292e'};
}

.skillzen-punctuation {
  color: ${themeName === 'dark' ? '#d1d5db' : '#586069'};
}

.skillzen-property {
  color: ${themeName === 'dark' ? '#60a5fa' : '#005cc5'};
}

.skillzen-method {
  color: ${themeName === 'dark' ? '#34d399' : '#22863a'};
}

/* Legacy pre/code support for backward compatibility */
pre {
  background-color: ${themeName === 'dark' ? '#1e293b' : '#f8fafc'};
  border: 1px solid ${themeName === 'dark' ? '#374151' : theme.accent_color};
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  margin: 16px 0;
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 14px;
  line-height: 1.6;
}

code {
  background-color: ${themeName === 'dark' ? '#374151' : '#f1f5f9'};
  color: ${themeName === 'dark' ? '#e5e7eb' : '#1e293b'};
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.875rem;
  border: 1px solid ${themeName === 'dark' ? '#4b5563' : '#e2e8f0'};
}

pre code {
  background: none;
  padding: 0;
  border: none;
  color: inherit;
}

ul {
  ${isRTL ? 'padding-right: 1.5rem;' : 'padding-left: 1.5rem;'}
}

li {
  margin-bottom: 0.25rem;
}

img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  margin: 1rem 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Responsive design */
@media (max-width: 768px) {
  body {
    padding: 10px;
  }

  .content-container {
    padding: 16px;
  }

  h1 {
    font-size: 1.875rem;
  }

  h2 {
    font-size: 1.5rem;
  }
}
`;
}

/**
 * Apply theme to HTML content
 * Replaces CSS in <style> tag with new theme CSS
 * Automatically detects RTL content (Arabic, Hebrew, etc.)
 */
export function applyTheme(html: string, themeName: string): string {
  // Extract title
  const title = extractTitle(html);

  // Extract body content
  const bodyContent = extractBodyContent(html);

  // Detect if content is RTL
  const isRTL = isRTLLanguage(bodyContent);
  const lang = isRTL ? 'ar' : 'en';
  const dir = isRTL ? 'rtl' : 'ltr';

  // Generate new themed CSS with RTL support
  const newCSS = generateThemeCSS(themeName, isRTL);

  // Build complete HTML with new theme and RTL support
  return `<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>${newCSS}</style>
</head>
<body>
  <div class="content-container">
    ${bodyContent}
  </div>
  <script>
function toggleAnswer(answerId, buttonElement) {
  const answerElement = document.getElementById(answerId);

  if (answerElement.style.display === 'none' || answerElement.style.display === '') {
    answerElement.style.display = 'block';
    buttonElement.textContent = '${isRTL ? 'إخفاء الإجابة' : 'Hide Answer'}';
    buttonElement.style.backgroundColor = '#dc3545';
  } else {
    answerElement.style.display = 'none';
    buttonElement.textContent = '${isRTL ? 'إظهار الإجابة' : 'Show Answer'}';
    buttonElement.style.backgroundColor = '#3b82f6';
  }
}
</script>
</body>
</html>`;
}

/**
 * Extract theme information from HTML for display
 */
export function getThemeInfo(html: string): { theme: string; config: ThemeConfig } {
  const theme = detectTheme(html);
  const config = AVAILABLE_THEMES[theme];
  return { theme, config };
}
